import React from 'react';
import { api } from '../api.js';
import * as T from '../time.js';
import { dateTimeLabel, mapApprovals } from '../mapping.js';
import { useTasks } from './use-tasks.js';
import {AppIcon, Avatar, Icon, PH, Panel, StatusPill} from './components.jsx';
import { mapApp, ME, NOTIFS } from './data.jsx';
import { HEALTH, openService } from './apps.js';
import { OPS_KIND, opsPeriod } from './ops.js';
import { AlertRow, Button, DataBar, DataTable, Metric, Pill } from './ds/fgcp-ds.jsx';

// home.jsx — Workspace 홈 (product theme console dashboard)
const HPill = Pill;
const HBtn = Button;
const WD_KR_SHORT = ['일','월','화','수','목','금','토'];
/** 상단 날짜 — 앱 시간대의 오늘 */
function todayStamp() {
  return `${T.todayYmd()} ${WD_KR_SHORT[T.parts().dow]}`;
}


/**
 * 홈 — 숫자와 항목은 서버 요약(GET /api/v1/home/summary — 모듈 요약 제공자를 모은 것, 계획 문서 5.3 3a) 하나로 그린다. 화면에서 집계하지 않는다.
 * refreshKey가 바뀌면(결재·업무 처리 뒤, 60초 주기) 다시 받는다
 */
function Home({ onNav, onOpen, onOpenApproval, onOpenNotif, onAllNotifs, onToast, refreshKey = 0, notifs = NOTIFS }) {
  const [home, setHome] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    let live = true;
    api.get('/api/v1/home/summary').then(h => { if (live) { setHome(h); setErr(''); } })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [refreshKey]);
  // 내 업무 목록: 체크 상태는 서버 값(체크는 먼저 보이고 저장 — 실패하면 되돌아간다)
  const { tasks, summary, isDone, toggle: toggleTask } = useTasks(onToast);
  if (!home) return <div className="body"><Panel><div data-home-loading style={{ padding: 16, fontSize: 12.5, color: err ? 'var(--crit)' : 'var(--ink-3)' }}>{err ? '홈 요약을 받지 못했습니다 — ' + err : '불러오는 중'}</div></Panel></div>;
  const approvalSummary = home.approvals;
  const myApprovals = mapApprovals(home.approvals.items);
  const t = summary;   // 업무 숫자는 목록과 같이 갱신되는 업무 요약(체크 뒤 다시 받는다 — 서버 taskSummary, 홈 요약의 tasks와 같은 함수)
  const annual = home.leave.balances.find(b => b.code === 'annual') ?? home.leave.balances[0] ?? null;
  const dueToday = summary.due_today;
  // 머리 배지 — 지금 보이는 장애가 있으면 '장애', 점검이면 '점검', 없으면 배지 없음(감시 결과가 아니므로 '정상'이라 말하지 않는다 — 계획 문서 5.3 3b)
  const top = home.opsNotices.find(n => n.kind === 'incident') ?? home.opsNotices.find(n => n.kind === 'maintenance');
  const opsBadge = top ? { kind: top.kind, ...OPS_KIND[top.kind] } : null;
  const weekDone = summary.week_done, weekTotal = summary.week_total;
  const pct = weekTotal ? Math.round((weekDone / weekTotal) * 100) : 0;
  const prio = { high: ['높음', 'crit'], mid: ['보통', 'warn'], low: ['낮음', 'neutral'] };

  const taskCols = [
    { key: 'chk', header: '', width: 34, render: t => (
      <button onClick={() => toggleTask(t)} disabled={!t.canUpdate} aria-label="완료 토글" style={{
        width: 16, height: 16, borderRadius: 'var(--r-sm)', display: 'grid', placeItems: 'center',
        border: '1px solid ' + (isDone(t) ? 'var(--ok)' : 'var(--line-strong)'),
        background: isDone(t) ? 'var(--ok)' : 'transparent', transition: 'all var(--dur-fast) var(--ease)' }}>
        {isDone(t) && <Icon name="check" size={10} color="var(--n-1000)" sw={2.6} />}
      </button>) },
    { key: 'title', header: '업무', strong: true, render: t => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isDone(t) ? 'line-through' : 'none', color: isDone(t) ? 'var(--ink-3)' : 'var(--ink)' }}>{t.title}</span>
        <HPill tone="neutral" dot={false}>{t.tag}</HPill>
      </span>) },
    { key: 'prog', header: '진행', align: 'right', width: 132, render: t => {
      const p = isDone(t) ? 100 : t.progress;
      return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        <DataBar pct={p} tone={isDone(t) ? 'ok' : 'accent'} width={56} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 26, textAlign: 'right' }}>{p}%</span>
      </span>; } },
    { key: 'prio', header: '우선', width: 68, render: t => <HPill tone={prio[t.priority][1]} dot>{prio[t.priority][0]}</HPill> },
    { key: 'due', header: '기한', align: 'right', width: 74, render: t => isDone(t)
      ? <HPill tone="ok" dot={false}>완료</HPill>
      : <HPill tone={t.due === '오늘' ? 'crit' : t.due === '내일' ? 'warn' : 'neutral'} dot={false}>{t.due}</HPill> },
  ];

  return (
    <React.Fragment>
      <div className="head">
        <h1>Workspace</h1>
        <div className="meta">
          {opsBadge && <HPill tone={opsBadge.tone} dot data-ops-badge={opsBadge.kind}>{opsBadge.label}</HPill>}
          <HPill tone="neutral" dot={false}><span className="mono">{todayStamp()}</span></HPill>
          <HPill tone="neutral" dot={false}><span className="mono">{ME.dept}</span></HPill>
        </div>
        <div className="r">
          <HBtn variant="secondary" size="sm" onClick={() => onNav('leave')}>휴가 신청</HBtn>
          <HBtn variant="primary" size="sm" onClick={() => onNav('approval')}>기안 작성</HBtn>
        </div>
      </div>

      <div className="body">
        <div className="kpis" data-home-kpis>
          <Metric label="결재 대기" value={approvalSummary.waiting} unit="건" delta={approvalSummary.waiting ? '조치 필요' : '없음'} tone={approvalSummary.waiting ? 'down' : 'neutral'} />
          <Metric label="진행 중 업무" value={t.doing} unit="건" delta={'기한 임박 ' + t.due_soon + (t.overdue ? ' · 지남 ' + t.overdue : '')} tone={t.overdue ? 'down' : t.due_soon ? 'neutral' : 'up'} />
          <Metric label={'잔여 ' + (annual ? annual.name : '휴가')} value={annual ? annual.remainingDays : 0} unit={'/ ' + (annual ? annual.grantedDays : 0) + '일'}
            delta={home.leave.pending.count ? '신청 중 ' + home.leave.pending.days + '일' : '사용 ' + (annual ? annual.usedDays : 0) + '일'} />
          <Metric label="오늘 내 예약" value={home.booking.today.length} unit="건" delta={'빈 회의실 ' + home.booking.rooms.free + '/' + home.booking.rooms.total} />
        </div>

        <div className="row2">
          <Panel>
            <PH title="내 업무" sub={summary.doing + '건 진행 · 이번 주 ' + weekDone + '/' + weekTotal + ' 완료'} right={
              <React.Fragment>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span className="bar" style={{ width: 92 }}><i style={{ width: pct + '%', background: 'var(--ok)' }} /></span>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{pct}%</span>
                </span>
                <button className="link" onClick={() => onNav('task')}>업무 보드<Icon name="chevron-right" size={13} /></button>
              </React.Fragment>} />
            <DataTable columns={taskCols} rows={tasks.map(t => ({ ...t, id: t.id }))}
              footer={<React.Fragment>
                <span>총 {tasks.length}건</span>
                {dueToday > 0 && <span style={{ color: 'var(--crit)' }}>오늘 마감 {dueToday}건</span>}
              </React.Fragment>} />
          </Panel>

          <Panel>
            <PH title="알림" sub={notifs.filter(n => n.unread).length + ' 미확인'} right={<button className="link" onClick={onAllNotifs}>전체</button>} />
            <div style={{ padding: 6 }}>
              {notifs.slice(0, 4).map((n, i) => (
                <AlertRow key={n.id} divider={i > 0}
                  severity={n.unread ? (n.kind === 'approval' ? 'warn' : 'info') : 'resolved'}
                  title={n.title} meta={[n.body.length > 26 ? n.body.slice(0, 26) + '…' : n.body, n.at]}
                  onClick={() => onOpenNotif(n)} data-home-notif={n.id} style={{ cursor: 'pointer' }} />
              ))}
            </div>
          </Panel>
        </div>

        <div className="row2">
          <Panel>
            <PH title="오늘 일정" sub={'내 예약·참석 ' + home.booking.today.length + '건'} right={<button className="link" onClick={() => onNav('booking')}>예약<Icon name="chevron-right" size={13} /></button>} />
            <TodaySchedule items={home.booking.today} onOpen={id => onOpen('booking', id)} />
            <div data-free-rooms style={{ padding: '8px 16px 12px', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink-2)' }}>
              지금 비어 있는 회의실 {home.booking.rooms.free}/{home.booking.rooms.total}
              {home.booking.rooms.names.length > 0 && <span style={{ color: 'var(--ink-3)' }}> · {home.booking.rooms.names.map(r => r.name).join(', ')}</span>}
            </div>
          </Panel>

          <Panel>
            <PH title="내 결재" sub={'대기 ' + approvalSummary.waiting + ' · 진행 ' + approvalSummary.in_progress + ' · 반려 ' + approvalSummary.rejected}
              right={<button className="link" onClick={() => onNav('approval')}>결재함<Icon name="chevron-right" size={13} /></button>} />
            {myApprovals.length === 0 && <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--ink-3)' }}>처리할 결재가 없습니다</div>}
            {myApprovals.map(a => (
              <button key={a.id} className="rowlist" onClick={() => onOpenApproval(a)}>
                <Avatar name={a.drafter} size={24} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</span>
                  <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2, whiteSpace: 'nowrap' }}>{a.form} · {a.amount}</span>
                </span>
                {a.urgent ? <HPill tone="crit" dot>긴급</HPill> : <StatusPill s={a.status} />}
              </button>
            ))}
            <div data-approval-done style={{ padding: '8px 16px 12px', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink-3)' }}>이번 달 완료 {approvalSummary.completed_this_month}건</div>
          </Panel>
        </div>

        <div className="row2">
          <Panel scroll>
            <PH title="공지 · 문서" sub={'안 읽은 공지 ' + home.noticeUnread + '건'} right={<button className="link" onClick={() => onNav('board')}>게시판<Icon name="chevron-right" size={13} /></button>} />
            <DataTable
              columns={[
                { key: 'board', header: '게시판', width: 96, render: p => <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.board_name}</span> },
                { key: 'title', header: '제목', strong: true, render: p => (
                  <button onClick={() => onOpen('board', p.id)} data-home-post={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, color: 'var(--ink)', textAlign: 'left' }}>
                    {p.importance === 'high' && <HPill tone="crit" dot={false}>중요</HPill>}
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: p.is_read ? 400 : 600 }}>{p.title}</span>
                    {!p.is_read && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--acc-graph)', flex: 'none' }} />}
                  </button>) },
                { key: 'author', header: '작성자', width: 92, render: p => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><Avatar name={p.author_name} size={20} />{p.author_name}</span> },
                { key: 'at', header: '등록', align: 'right', width: 112, render: p => <span className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{dateTimeLabel(p.created_at).slice(5)}</span> },
              ]}
              rows={home.notices.map(p => ({ ...p, id: p.id }))} />
          </Panel>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Panel>
              <PH title="앱" sub="즐겨찾기 · 최근 사용" right={<button className="link" onClick={() => onNav('__launcher')}>전체</button>} />
              <div data-home-apps style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, padding: 12 }}>
                {home.apps.length === 0 && <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--ink-3)', padding: 6 }}>전체 앱에서 별을 누르거나 서비스를 열면 여기에 보입니다</div>}
                {home.apps.map(mapApp).map(app => (
                  <button key={app.id} className="tile" data-home-app={app.id} onClick={() => openService(app, onToast)} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <AppIcon name={app.icon} size={15} color="var(--acc)" />
                    <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '0 1 auto' }}>{app.name}</span>
                    {app.favorite && <Icon name="star" size={11} color="var(--acc)" />}
                    {app.health && HEALTH[app.health] && <HPill tone={HEALTH[app.health][1]} dot>{HEALTH[app.health][0]}</HPill>}
                  </button>
                ))}
              </div>
            </Panel>
            <Panel>
              <PH title="운영 공지" sub={home.opsNotices.length ? home.opsNotices.length + '건' : ''} />
              <div data-home-ops style={{ padding: 6 }}>
                {home.opsNotices.length === 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--ink-3)' }}>진행 중인 운영 공지 없음</div>}
                {home.opsNotices.map((o, i) => (
                  <AlertRow key={o.id} divider={i > 0} severity={OPS_KIND[o.kind].severity} data-ops-notice={o.id}
                    title={OPS_KIND[o.kind].label + ' · ' + o.title} meta={[o.body || (o.service_name ?? ''), opsPeriod(o)].filter(Boolean)} />
                ))}
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

// 오늘 내 일정(서버 항목 — 예약자이거나 참석자, 확정·승인 대기, 시작 순) — 지난 항목은 흐리게, 현재 시각 마커 삽입. 시각은 앱 시간대
function TodaySchedule({ items, onOpen }) {
  const now = Date.now();
  const nowLabel = T.hm(now);
  const hm = T.hm;
  const nowIdx = items.findIndex(b => new Date(b.ends_at).getTime() > now);
  if (items.length === 0) return <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--ink-3)' }}>오늘 예약·참석할 일정이 없습니다</div>;
  return (
    <div data-today-schedule style={{ padding: '4px 0 8px' }}>
      {items.map((b, i) => {
        const isMine = b.requester_user_id === ME.id;
        const past = new Date(b.ends_at).getTime() <= now;
        return (
          <React.Fragment key={b.id}>
            {i === nowIdx && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 16px' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--crit)', flex: 'none' }} />
                <span className="mono" style={{ fontSize: 10, fontWeight: 600, color: 'var(--crit)', letterSpacing: '.06em' }}>{'NOW ' + nowLabel}</span>
                <span style={{ flex: 1, height: 1, background: 'var(--crit)', opacity: .3 }} />
              </div>
            )}
            <button onClick={() => onOpen(b.id)} data-today-booking={b.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '7px 16px', opacity: past ? .45 : 1, minWidth: 0, width: '100%', textAlign: 'left', color: 'var(--ink)' }}>
              <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-2)', width: 82, flex: 'none', whiteSpace: 'nowrap' }}>{hm(b.starts_at)}–{hm(b.ends_at)}</span>
              <span style={{ width: 2, alignSelf: 'stretch', borderRadius: 1, background: isMine ? 'var(--acc-graph)' : 'var(--line-strong)', flex: 'none' }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.title}</span>
                <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)', marginTop: 1, whiteSpace: 'nowrap' }}>{b.facility_name} · {b.requester_name}</span>
              </span>
              {b.status === 'pending_approval' && <HPill tone="warn" dot={false}>승인 대기</HPill>}
              {isMine ? <HPill tone="accent" dot={false}>내 예약</HPill> : <HPill tone="neutral" dot={false}>참석</HPill>}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export { Home, TodaySchedule };
